import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, ShoppingBag, User, Globe, Sparkles } from "lucide-react";
import { Logo } from "./Logo";
import { useI18n, LANGS } from "../i18n";
import { useCart } from "../cart";
import { useAuth } from "../auth";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "./ui/dropdown-menu";

const NavLink = ({ to, children, testid }) => (
  <Link to={to} data-testid={testid} className="text-sm font-medium text-slate-700 hover:text-primary transition-colors">
    {children}
  </Link>
);

export const Header = () => {
  const { t, lang, setLang } = useI18n();
  const { count } = useCart();
  const { user, doLogout } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isManager = user && ["super_admin", "admin", "staff"].includes(user.role);
  const isOperator = user && user.role === "operator";

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-white/90 backdrop-blur-md border-b border-slate-100 shadow-sm" : "bg-white/60 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <Logo />
        <nav className="hidden lg:flex items-center gap-7">
          <NavLink to="/esperienze" testid="nav-experiences">{t("nav.experiences")}</NavLink>
          <NavLink to="/eventi" testid="nav-eventi">Eventi</NavLink>
          <NavLink to="/vieste" testid="nav-vieste">Vieste</NavLink>
          <NavLink to="/shop" testid="nav-shop">{t("nav.shop")}</NavLink>
          <NavLink to="/alloggi" testid="nav-accommodations">Alloggi</NavLink>
          <NavLink to="/domani" testid="nav-tomorrow">{t("nav.tomorrow")}</NavLink>
          <NavLink to="/last-minute" testid="nav-lastminute">{t("nav.lastminute")}</NavLink>
          <NavLink to="/transfer" testid="nav-transfers">{t("nav.transfers")}</NavLink>
          <NavLink to="/dove-mangiare" testid="nav-eat">{t("nav.eat")}</NavLink>
          <Link to="/concierge" data-testid="nav-concierge" className="flex items-center gap-1.5 text-sm font-semibold text-secondary hover:text-primary transition-colors">
            <Sparkles className="h-4 w-4" /> {t("nav.concierge")}
          </Link>
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger data-testid="lang-switcher" className="flex items-center gap-1 rounded-full px-2.5 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-100 transition-colors">
              <Globe className="h-4 w-4" /> {lang.toUpperCase()}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {LANGS.map((l) => (
                <DropdownMenuItem key={l.code} data-testid={`lang-${l.code}`} onClick={() => setLang(l.code)}>
                  {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Link to="/vacanza" data-testid="cart-button" className="relative rounded-full p-2 hover:bg-slate-100 transition-colors">
            <ShoppingBag className="h-5 w-5 text-slate-700" />
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-accent text-white text-[10px] font-bold flex items-center justify-center">
                {count}
              </span>
            )}
          </Link>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="account-menu" className="rounded-full p-2 hover:bg-slate-100 transition-colors">
                <User className="h-5 w-5 text-slate-700" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5 text-xs text-slate-500 truncate">{user.email}</div>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-account" onClick={() => navigate("/account")}>{t("nav.account")}</DropdownMenuItem>
                {isManager && <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/gestione")}>{t("nav.admin")}</DropdownMenuItem>}
                {isManager && <DropdownMenuItem data-testid="menu-store" onClick={() => navigate("/store")}>{t("nav.store")}</DropdownMenuItem>}
                {isOperator && <DropdownMenuItem data-testid="menu-operator" onClick={() => navigate("/operatore")}>{t("nav.operator")}</DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await doLogout(); navigate("/"); }}>{t("nav.logout")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/accedi" data-testid="nav-login" className="hidden sm:inline-flex rounded-full bg-primary text-white text-sm font-semibold px-5 py-2 hover:bg-primary/90 transition-colors">
              {t("nav.login")}
            </Link>
          )}

          <button data-testid="mobile-menu-toggle" className="lg:hidden rounded-full p-2 hover:bg-slate-100" onClick={() => setOpen(!open)}>
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-slate-100 px-4 py-4 space-y-3 animate-float-up" data-testid="mobile-menu">
          {[["/esperienze", t("nav.experiences")], ["/eventi", "Eventi"], ["/vieste", "Vieste"], ["/shop", t("nav.shop")], ["/alloggi", "Alloggi"], ["/domani", t("nav.tomorrow")], ["/last-minute", t("nav.lastminute")],
            ["/transfer", t("nav.transfers")], ["/dove-mangiare", t("nav.eat")], ["/guide", t("nav.guides")],
            ["/concierge", t("nav.concierge")], ["/partner", t("nav.partner")]].map(([to, label]) => (
            <Link key={to} to={to} onClick={() => setOpen(false)} className="block text-sm font-medium text-slate-700 py-1.5">{label}</Link>
          ))}
          {!user && <Link to="/accedi" onClick={() => setOpen(false)} className="block rounded-full bg-primary text-white text-sm font-semibold px-5 py-2.5 text-center">{t("nav.login")}</Link>}
        </div>
      )}
    </header>
  );
};
